<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TemplateSelector from 'dashboard/components-next/agents/TemplateSelector.vue';
import ServicePlaybook from 'dashboard/components-next/agents/ServicePlaybook.vue';
import TemplateTools from 'dashboard/components-next/agents/TemplateTools.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const accountId = computed(() => route.params.accountId);
const isSubmitting = ref(false);

// Tela única: escolher um ponto de partida (template) + nome -> criar.
// O modelo é escolhido como "Lia X.X" no canvas (mascarado) e todo o ajuste
// fino (prompt, ferramentas, comportamento) acontece depois no editor visual.
// O template já pré-preenche system_prompt/welcome/tools/temperatura.
const selectedTemplateId = ref(null);
const selectedTemplate = ref(null);

const formData = ref({
  name: '',
  description: '',
  agent_plan_id: null,
  system_prompt: '',
  welcome_message: '',
  active: true,
  agent_type: 'llm',
  temperature: 0.7,
  max_tokens: null,
  tools: [],
  sector: null,
  template_id: null,
  role: '',
  goal: '',
  service_playbook: {},
});

const backUrl = computed(() => ({
  name: 'ai_agents_list',
  params: { accountId: accountId.value },
}));

const isValid = computed(() => formData.value.name.trim().length >= 3);

// Apply template when selected (pre-fills the whole agent config)
const handleTemplateSelect = template => {
  const previousName = selectedTemplate.value?.name;
  selectedTemplate.value = template;

  if (template) {
    const config = template.default_config || {};
    formData.value = {
      ...formData.value,
      name:
        !formData.value.name || formData.value.name === previousName
          ? template.name || ''
          : formData.value.name,
      description: template.description || '',
      system_prompt: template.system_prompt || '',
      welcome_message: template.welcome_message || '',
      temperature: config.temperature ?? 0.7,
      max_tokens: config.max_tokens || null,
      tools: [...(template.recommended_tools || [])],
      service_playbook: { ...template.service_playbook },
      sector: template.id,
      template_id: template.id,
    };
  } else {
    formData.value = {
      ...formData.value,
      description: '',
      system_prompt: '',
      welcome_message: '',
      temperature: 0.7,
      max_tokens: null,
      tools: [],
      sector: null,
      template_id: null,
      service_playbook: {},
    };
  }
};

watch(selectedTemplateId, newVal => {
  formData.value.template_id = newVal;
  formData.value.sector = newVal;
});

onMounted(() => {
  store.dispatch('aiAgents/get');
});

const handleSubmit = async () => {
  if (!isValid.value) {
    useAlert(t('AI_AGENTS.VALIDATION.NAME_REQUIRED'));
    return;
  }

  try {
    isSubmitting.value = true;
    const agent = await store.dispatch('aiAgents/create', formData.value);
    useAlert(t('AI_AGENTS.MESSAGES.CREATE_SUCCESS'));
    router.push({
      name: 'ai_agents_settings',
      params: { accountId: accountId.value, agentId: agent.id },
    });
  } catch (error) {
    useAlert(error?.message || t('AI_AGENTS.MESSAGES.CREATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  router.push(backUrl.value);
};
</script>

<template>
  <div
    class="flex flex-col w-full h-full min-h-0 bg-n-background overflow-hidden"
  >
    <!-- Header -->
    <header
      class="sticky top-0 z-10 bg-n-background/95 backdrop-blur border-b border-n-weak px-4 sm:px-6"
    >
      <div class="w-full py-4">
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="p-2 -ml-2 rounded-lg hover:bg-n-alpha-2 transition-colors"
            @click="handleCancel"
          >
            <span class="i-lucide-arrow-left w-5 h-5 text-n-slate-11" />
          </button>
          <div>
            <h1 class="text-lg font-semibold text-n-slate-12">
              {{ t('AI_AGENTS.CREATE.TITLE') }}
            </h1>
            <p class="text-sm text-n-slate-11 hidden sm:block">
              {{ t('AI_AGENTS.CREATE.SUBTITLE') }}
            </p>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1 w-full px-4 sm:px-6 py-8 overflow-y-auto">
      <div class="max-w-5xl mx-auto space-y-10">
        <!-- Passo 1: ponto de partida -->
        <section class="space-y-4">
          <div class="flex items-start gap-3">
            <span
              class="flex size-7 shrink-0 items-center justify-center rounded-full bg-n-teal-3 text-sm font-semibold text-n-teal-11"
            >
              {{ 1 }}
            </span>
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.CREATE.STEP1_NEW.TITLE') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('AI_AGENTS.CREATE.STEP1_NEW.DESCRIPTION') }}
              </p>
            </div>
          </div>

          <TemplateSelector
            v-model="selectedTemplateId"
            @select="handleTemplateSelect"
          />
        </section>

        <!-- Passo 2: identidade -->
        <section class="space-y-4">
          <div class="flex items-start gap-3">
            <span
              class="flex size-7 shrink-0 items-center justify-center rounded-full bg-n-teal-3 text-sm font-semibold text-n-teal-11"
            >
              {{ 2 }}
            </span>
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.CREATE.STEP_IDENTITY.TITLE') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('AI_AGENTS.CREATE.STEP_IDENTITY.DESCRIPTION') }}
              </p>
            </div>
          </div>

          <div class="bg-n-solid-2 rounded-2xl p-6 border border-n-weak">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                v-model="formData.name"
                :label="t('AI_AGENTS.FORM.NAME')"
                :placeholder="t('AI_AGENTS.FORM.NAME_PLACEHOLDER')"
                required
              />
              <Input
                v-model="formData.description"
                :label="t('AI_AGENTS.FORM.DESCRIPTION')"
                :placeholder="t('AI_AGENTS.FORM.DESCRIPTION_PLACEHOLDER')"
              />
            </div>
            <div
              class="mt-4 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4"
            >
              <p class="flex items-center gap-2 text-xs text-n-slate-10">
                <span class="i-lucide-wand-2 w-3.5 h-3.5 shrink-0" />
                {{ t('AI_AGENTS.CREATE.POST_CREATE_HINT') }}
              </p>
              <p class="flex items-center gap-2 text-xs text-n-violet-11">
                <span class="i-lucide-shield-check w-3.5 h-3.5 shrink-0" />
                {{ t('AI_AGENTS.TEMPLATES.PROTECTED_HINT') }}
              </p>
            </div>
          </div>
        </section>
        <ServicePlaybook v-model="formData.service_playbook" />
        <TemplateTools
          v-if="selectedTemplate"
          v-model="formData.tools"
          :template="selectedTemplate"
        />
        <p class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.PLAYBOOK.SETUP_HINT') }}
        </p>
      </div>
    </main>

    <!-- Footer: botões alinhados à coluna de conteúdo (não colam na borda,
         nem ficam atrás do balão de ajuda) -->
    <footer
      class="sticky bottom-0 bg-n-background/95 backdrop-blur border-t border-n-weak px-4 sm:px-6"
    >
      <div class="max-w-5xl mx-auto py-4">
        <div class="flex items-center justify-between gap-3">
          <p
            v-if="selectedTemplate"
            class="hidden sm:flex items-center gap-2 text-xs text-n-slate-11"
          >
            <span class="i-lucide-layout-template w-3.5 h-3.5" />
            {{ selectedTemplate.name }}
          </p>
          <span v-else class="hidden sm:block" />
          <div class="flex items-center gap-3">
            <Button
              :label="t('AI_AGENTS.ACTIONS.CANCEL')"
              variant="ghost"
              color="slate"
              class="hidden sm:flex"
              @click="handleCancel"
            />
            <Button
              :label="t('AI_AGENTS.ACTIONS.CREATE_AGENT')"
              :is-loading="isSubmitting"
              :disabled="!isValid"
              icon="i-lucide-sparkles"
              @click="handleSubmit"
            />
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>
